import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  completeAnalyticsNewPassword,
  loadAnalyticsSession,
  signInAnalyticsOperator,
  signOutAnalyticsOperator,
  type AnalyticsOperator,
  type AnalyticsSession,
} from '@/lib/authService';
import { AnalyticsAuthContext, type AuthStatus } from '@/lib/useAnalyticsAuth';

const errorMessage = (error: unknown, fallback: string): string => (error instanceof Error && error.message ? error.message : fallback);

export const AnalyticsAuthProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [operator, setOperator] = useState<AnalyticsOperator | null>(null);
  const [error, setError] = useState<string | null>(null);

  const applySession = useCallback((session: AnalyticsSession): void => {
    setStatus(session.status);
    setOperator(session.status === 'authenticated' || session.status === 'unauthorized' ? session.operator : null);
    setError(null);
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    setStatus('loading');
    try {
      applySession(await loadAnalyticsSession());
    } catch (nextError) {
      setOperator(null);
      setStatus('error');
      setError(errorMessage(nextError, 'Unable to load analytics session'));
    }
  }, [applySession]);

  useEffect(() => {
    let active = true;

    const loadInitialSession = async (): Promise<void> => {
      try {
        const session = await loadAnalyticsSession();
        if (active) {
          applySession(session);
        }
      } catch (nextError) {
        if (active) {
          setOperator(null);
          setStatus('error');
          setError(errorMessage(nextError, 'Unable to load analytics session'));
        }
      }
    };

    void loadInitialSession();

    return () => {
      active = false;
    };
  }, [applySession]);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      setStatus('loading');
      try {
        const result = await signInAnalyticsOperator(email, password);
        if (result.status === 'newPasswordRequired') {
          setOperator(null);
          setStatus('newPasswordRequired');
          setError(null);
          return;
        }
        applySession(await loadAnalyticsSession());
      } catch (nextError) {
        setOperator(null);
        setStatus('unauthenticated');
        setError(errorMessage(nextError, 'Unable to sign in'));
      }
    },
    [applySession],
  );

  const completeNewPassword = useCallback(
    async (password: string): Promise<void> => {
      setStatus('loading');
      try {
        const result = await completeAnalyticsNewPassword(password);
        if (result.status !== 'signedIn') {
          setOperator(null);
          setStatus('newPasswordRequired');
          return;
        }
        applySession(await loadAnalyticsSession());
      } catch (nextError) {
        setOperator(null);
        setStatus('newPasswordRequired');
        setError(errorMessage(nextError, 'Unable to set the new password'));
      }
    },
    [applySession],
  );

  const logout = useCallback(async (): Promise<void> => {
    setStatus('loading');
    try {
      await signOutAnalyticsOperator();
    } finally {
      setOperator(null);
      setStatus('unauthenticated');
      setError(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      completeNewPassword,
      error,
      login,
      logout,
      operator,
      reload,
      status,
    }),
    [completeNewPassword, error, login, logout, operator, reload, status],
  );

  return <AnalyticsAuthContext.Provider value={value}>{children}</AnalyticsAuthContext.Provider>;
};
